/**
 * @jsxRuntime classic
 * @jsx jsx
 */
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { jsx } from '@emotion/react';
import { useIntl } from 'react-intl-next';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import VisuallyHidden from '@atlaskit/visually-hidden';
import { AI_MODAL_SCREEN_CLASS } from '../../../utils/constants';
import LoadingIcon from '../../assets/Loading';
import { CancelEscButton } from '../../components/CancelEscButton/CancelEscButton';
import { FloatingContainer } from '../../components/FloatingContainer/FloatingContainer';
import { defaultPaddingStyles } from '../../components/FloatingContainer/styles';
import { Footer } from '../../components/Footer/Footer';
import { FooterOld } from '../../components/Footer/FooterOld';
import { LatestPromptHeader } from '../../components/LatestPromptHeader/LatestPromptHeader';
import { PoweredByRovo } from '../../components/PoweredByRovo/PoweredByRovo';
import { layoutBorderStyles } from '../../styles/renderer';
import messages from './messages';
import { layoutStyles, logoStyles, previewContainer, titleStyles } from './styles';

/**
 * Could be either:
 * - userInput only (free form generate / interrogate)
 * - presetTitle only (summarize)
 * - both presetTitle and userInput (brainstorm)
 */

export var LoadingScreen = function LoadingScreen(_ref) {
  var parentPresetPromptLabel = _ref.parentPresetPromptLabel,
    childShortTitle = _ref.childShortTitle,
    presetPromptLabel = _ref.presetPromptLabel,
    title = _ref.title,
    latestPrompt = _ref.latestPrompt,
    preview = _ref.preview,
    providerFactory = _ref.providerFactory,
    schema = _ref.schema,
    onCancel = _ref.onCancel,
    agent = _ref.agent,
    disclaimer = _ref.disclaimer,
    additionalFooterText = _ref.additionalFooterText;
  var _useIntl = useIntl(),
    formatMessage = _useIntl.formatMessage;
  return jsx(FloatingContainer, {
    "data-testid": "loading-screen"
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    ,
    className: "loading-screen ".concat(AI_MODAL_SCREEN_CLASS),
    rainbowBorderAnimation: true,
    disablePadding: true
  }, jsx(VisuallyHidden, null, formatMessage(messages.loadingAriaTitle)), jsx(LatestPromptHeader, {
    parentPresetPromptLabel: parentPresetPromptLabel,
    childShortTitle: childShortTitle,
    prompt: latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.userInput,
    promptADF: latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.userADFInput,
    presetTitle: latestPrompt === null || latestPrompt === void 0 ? void 0 : latestPrompt.presetTitle,
    providerFactory: providerFactory,
    schema: schema,
    agent: agent,
    showBack: true
  }), preview &&
  /*
   * We use a live region so that content updates will be announced to screenreaders. We set:
   * - aria-atomic="false": because if aria-atomic were set to true, the screen reader would keep
   * reannouncing the content from the beginning rather than providing updates only for the newly added content.
   * - aria-live="polite": to ensure that updates are announced by screen readers in a non-disruptive manner,
   * allowing the user to finish their current task before the new content is communicated.
   *
   * We also add a visually hidden description for the live region ("Live updating content:") so that
   * there is at least some minimal context presented to the user about the incoming updates.
   */
  jsx("div", {
    "data-testid": "adf-content-live-region",
    "aria-atomic": "false",
    "aria-live": "polite"
    /* eslint-disable @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766 */,
    css: [previewContainer, defaultPaddingStyles, editorExperiment('platform_editor_ai_ai_button_block_elements', 'test') && layoutBorderStyles]
    /* eslint-enable */
  }, jsx(VisuallyHidden, {
    testId: "description"
  }, formatMessage(messages.liveRegionDescription)), preview), jsx("div", {
    css: [defaultPaddingStyles, layoutStyles]
  }, jsx("div", {
    "data-testid": "loading-icon",
    css: logoStyles
  }, jsx(LoadingIcon, {
    alt: formatMessage(messages.iconAltText)
  })), jsx("div", {
    css: titleStyles
  }, title), jsx(CancelEscButton, {
    autoFocus: true,
    onCancel: onCancel
  })), editorExperiment('platform_editor_ai_command_palate_improvement', 'test') && (editorExperiment('platform_editor_ai_command_palette_post_ga', 'test') ? jsx(Footer, {
    brand: agent ? 'rovo' : 'atlassian-intelligence',
    disclaimer: disclaimer,
    showDisclaimer: true
  }) : jsx(FooterOld, {
    disclaimer: disclaimer,
    additionalFooterText: additionalFooterText,
    footerRightContent: agent ? jsx(PoweredByRovo, null) : null
  })));
};